import logger from "../utils/LoggerFrontend";
import Reflux from "reflux";
import moment from "moment";
import { t } from "i18next";

// eslint-disable-next-line import/no-unresolved
import { CustomerSucessController } from "@talentbait/shared-components";

import Actions, { callWebUtils } from "../Actions";
import { cleanTitle, getLeadFormLink } from "../utils/Helpers";
import {
  getDisplayLink,
  prepareNewPosts,
  getImageSpecs,
  getCreativeUrlForOtherChannel
} from "../PagesUser/SelfServiceCampaignCreation/Previews/utils";
import { dbPostsToPreviewData } from "../PagesUser/SelfServiceCampaignCreation/Previews/componentUtils";
import database from "../utils/FirebaseController";
import { channelsByShortName } from "../data/app__channels";
import { formatNewPosts, formatPostPreviewData, getPictureArray } from "./utils";
import PerformanceEditorActions from "./../PagesUser/SelfServiceCampaignCreation/Previews/campaign-create-performance-editor/PerformanceEditorActions";
import { getLanguageByLocale } from "../PagesUser/ad-assistant/utils";
import { CHANNELS_WITHOUT_CREATIVES } from "../common/constants/channelsWithoutCreatives";

const DEFAULT_PAGE = {
  pageName: "JobHappier",
  pageId: "716047581885055",
  pageLogo: "/images/jobhappier.png"
};

const DEFAULT_JOB_GUARDIAN_PAGE = {
  pageName: "Jobguardian",
  pageId: "432296326632224",
  pageLogo: "https://graph.facebook.com/432296326632224/picture?type=square"
};

const DEFAULT_HILTE_JEHLE_PAGE = {
  pageName: "Hilti & Jehle GmbH",
  pageId: "1103985872976384",
  pageLogo: "https://graph.facebook.com/1103985872976384/picture?type=square"
};

export const defaultTeaserTemplates = [
  "Auf der Suche nach einer neuen Herausforderung? Das trifft sich gut – Wir suchen zur Verstärkung unseres Teams einen {{jobTitle}}",
  "🎯 Zur Verstärkung unseres Teams suchen wir ab sofort eine/-n {{jobTitle}}",
  "❗️Fehlanzeige❗️ Hier nicht: Wir suchen für unser Team einen {{jobTitle}}, mit dem wir loslegen können.",
  "Bei uns gibt es keinen Stillstand, sondern Fortschritt! Wir suchen einen {{jobTitle}}, mit dem wir die Zukunft gestalten können."
];

const chatgptDisabledChannels = ["indeed", "linkedin-feed", "linkedin"];

const defaultState = {
  loading: false,
  selectedPostNumber: 1,
  selectedPostData: "",
  loadingTeaserOptions: false,
  teaserOptions: {
    templatePanel: {},
    websitePanel: {},
    historicalPanel: {}
  },
  defaultTeaserTemplates,
  posts: null,
  channelsData: null,
  released: {
    posts: null,
    channelsData: null
  },
  payload: null,
  campaignData: {},
  jobCategoryData: {},
  adsetData: {},
  adsets: [],
  defaultOpenTeaserEditor: false,
  allowMultipleCreativesPerPost: false,
  allowMultiplePostsPerChannel: false,
  maxPostPerChannel: 2,
  loadingFacebookPages: false,
  facebookPages: [],
  campaignCreationLoadingState: {},
  jobPageLink: "",
  campaignManagers: [],
  subdomain: "app",
  linkedInPageId: null,
  companyLogo: "",
  adErrors: [],
  isRegeneratingText: false,
  defaultCreatives: [],
  templateDataToDownload: null
};

const CHANNELS_WITH_DYNAMIC_ADS = ["facebook", "google-display", "youtube", "ebay-kleinanzeigen"];
const META_CHANNELS = ["facebook", "instagram"];
const META_SHARED_ATTRIBUTES = ["pageName", "pageId", "url"];

export default class PreviewStore extends Reflux.Store {
  constructor() {
    super();
    logger.debug("PreviewStore:constructor", "called");

    this.state = Object.assign({}, defaultState);
    this.listenables = Actions;
  }

  onUpdateTemplateDataToDownload(templateDataToDownload) {
    this.setState({ templateDataToDownload });
  }

  onCreateCampaignDraftCompleted({ draftCampaignId, newPosts }) {
    logger.log("PreviewStore:onCreateCampaignDraftCompleted", "called...", {
      draftCampaignId,
      newPosts
    });

    const newPostsFormatted = formatNewPosts(newPosts);

    this.setState({
      posts: newPostsFormatted.posts,
      channelsData: newPostsFormatted.channelsData
    });
  }

  // Updates current posts with new data in the db
  async updatePostsDB({ userName, released, staging }) {
    const campaignId = this.state.campaignData.id;

    logger.log("PreviewStore:updatePostsDB", "called", {
      campaignId
    });

    if (campaignId == null) return;

    // POSTS VERSION: releasedVersion.stagingVersion (e.g.: 2.21)
    // increment released version if released posts are updated (if released post are update, staging posts are also updated)
    // increment staging version if we are not updating released posts, otherwise decrease staging version to 0
    const releasedVersion = (this.state.releasedVersion || 1) + (released ? 1 : 0);
    const stagingVersion = !released ? (this.state.stagingVersion || 0) + 1 : 0;

    const getCreatives = ({ isStaging }) => {
      const updatedDate = new Date().toISOString();

      // Select posts and channelsData based on the staging state
      const posts = isStaging ? this.state.posts : this.state.released.posts;
      const channelsData = isStaging ? this.state.channelsData : this.state.released.channelsData;
      const orgaId = this.state.campaignData.organizationId;

      logger.log("PreviewStore:getCreatives", "called", {
        posts,
        channelsData,
        orgaId
      });

      // Extract creatives for each channel and flatten the result into a single array
      return Object.keys(posts).flatMap((channelShortName) =>
        posts[channelShortName].map((post) => {
          // Find channelData for this post number
          const editableChannelData =
            channelsData[channelShortName].find((channel) => {
              return channel.postNumber === post.postNumber;
            }) ?? channelsData[channelShortName][0]; // If there is no channelData for this post, use the first channelData this is for old campaigns

          // Return a modified creative object
          return {
            ...post,
            updatedDate,
            ...(userName ? { lastUpdatedBy: userName } : {}),
            type: isStaging ? "staging" : "released",
            channel: channelShortName,
            channelData: editableChannelData,
            releasedVersion,
            stagingVersion,
            orgaId
          };
        })
      );
    };

    logger.log("PreviewStore:updatePostsDB", "finished saving posts", {
      campaignId
    });

    // Update creatives at DB
    await database.updateAdCreatives([
      ...(staging ? getCreatives({ isStaging: true }) : []),
      ...(released ? getCreatives({ isStaging: false }) : [])
    ]);

    // Save new post versions to store
    this.setState({
      releasedVersion,
      stagingVersion
    });

    // Update deployed status (use can see if staging is different from released)
    if (released) {
      Actions.updateCampaignDeployedStatus(campaignId, true);
    } else {
      Actions.updateCampaignDeployedStatus(campaignId, false);
    }
  }

  // copies over everything from staging to release or release to staging
  // if type === "released" we deploy staging posts to released
  // if type === "staging" we save all posts to staging and released
  async deployPosts({ userName, posts, channelsData, campaignId, type = "released" }) {
    logger.log("PreviewStore:deployPosts", "called", {
      campaignId,
      userName,
      posts,
      channelsData,
      type,
      releasedPosts: this.state.released,
      stagingPosts: this.state.posts
    });

    const updatedDate = new Date().toISOString();
    const oldPosts = type === "released" ? this.state.released.posts : this.state.posts;
    logger.debug("PreviewStore:deployPosts", "old posts are", {
      campaignId,
      type,
      oldPosts
    });
    // create new post db objects
    const releasedVersion = (this.state.releasedVersion || 1) + (type === "released" ? 1 : 0);
    const stagingVersion = 0;

    let newPosts = Object.keys(posts).reduce(
      (postsAcc, channelShortName) => [
        ...postsAcc,
        ...posts[channelShortName].map((post) => {
          const foundChannelDataByPostNumber = (channelsData[channelShortName] || []).find(
            (channelData) => channelData.postNumber === post.postNumber
          );

          return {
            ...post, // update post data
            updatedDate, // update date
            ...(userName ? { lastUpdatedBy: userName } : {}), // updater user
            channel: channelShortName, // add channel to the post because we create new released posts
            channelData: foundChannelDataByPostNumber || {}, // update channel data or default to an empty object
            releasedVersion,
            stagingVersion,
            type
          };
        })
      ],
      []
    );

    newPosts = Array.from(
      new Map(newPosts.map((post) => [post.postNumber + post.channel + post.type, post])).values()
    );

    // get old post ids to delete
    const oldPostIds = Object.keys(oldPosts).reduce(
      (idsAcc, channelShortName) => [
        ...idsAcc,
        ...oldPosts[channelShortName].map((post) => post.id)
      ],
      []
    );

    const duplicateMediaAssets = async (mediaAssetIds) => {
      let newMediaAssets = [];

      const context = {
        failed: (err) => {
          return newMediaAssets;
        },
        completed: ({ payload }) => {
          newMediaAssets = payload;
          return newMediaAssets;
        }
      };

      const mediaAssetsIdsToDuplicate = { mediaAssetIds: mediaAssetIds };
      await callWebUtils(context, "duplicateMultipleMediaAssets", [mediaAssetsIdsToDuplicate]);
      return newMediaAssets;
    };

    const newGeneratedPosts = await Promise.all(
      newPosts.map(async (post) => {
        const oldMediaAssetsId = post.mediaAssets?.map((mediaAssetId) => mediaAssetId) || [];
        const newMediaAssetIds =
          oldMediaAssetsId.length > 0
            ? await duplicateMediaAssets(oldMediaAssetsId).then((result) => {
                return result.duplicatedMediaAssetIds;
              })
            : [];

        // Otherwise, we would be adding the staging id as a field to the released post
        const { id, ...postWithoutId } = post;

        return {
          ...postWithoutId,
          mediaAssets: newMediaAssetIds,
          type
        };
      })
    );

    // Save new posts
    const savedPosts = await database.saveAdCreatives(newGeneratedPosts);

    if (type === "released") {
      logger.debug("PreviewStore:deployPosts", "updateAdCreatives released", {
        campaignId,
        oldPostIds,
        newPosts,
        oldPosts,
        savedPosts
      });

      const newGeneratedReleasedPosts = await Promise.all(
        newPosts.map(async (post) => {
          const oldMediaAssetsId = post.mediaAssets?.map((mediaAssetId) => mediaAssetId) || [];
          const newMediaAssetIds =
            oldMediaAssetsId.length > 0
              ? await duplicateMediaAssets(oldMediaAssetsId).then((result) => {
                  return result.duplicatedMediaAssetIds;
                })
              : [];

          return {
            ...post,
            mediaAssets: newMediaAssetIds,
            releasedVersion,
            stagingVersion
          };
        })
      );

      await database.updateAdCreatives(newGeneratedReleasedPosts);
    }

    // Update local post versions
    this.setState({
      releasedVersion,
      stagingVersion
    });

    logger.debug("PreviewStore:deployPosts", "saved new posts and updating existing ones", {
      campaignId,
      oldPostIds,
      newPosts,
      oldPosts,
      savedPosts
    });

    // Delete old posts
    await database.deleteAdCreatives(oldPostIds);

    // Update deployed status on campaign
    Actions.updateCampaignDeployedStatus(this.state.campaignData.id, true);
    // Return new posts, channel data

    return savedPosts.reduce(
      (acc, { type, channel, channelData, ...postData }) => ({
        ...acc,
        posts: {
          ...acc.posts,
          [channel]: [...(acc.posts[channel] || []), postData]
        },
        channelsData: {
          ...acc.channelsData,
          [channel]: [...(acc.channelsData[channel] || []), channelData]
        }
      }),
      {
        posts: {},
        channelsData: {}
      }
    );
  }

  onSetAdErrors({ hasError, errorMessage, inputKey }) {
    // Check if an error with the same channel already exists
    const errorExists = this.state.adErrors.some((error) => error.inputKey === inputKey);

    // If error with the same channel doesn't exist, add the new error
    if (!errorExists) {
      this.setState({
        adErrors: [...this.state.adErrors, { inputKey, hasError, errorMessage }]
      });
    }
  }

  onRemoveAdErrors(inputKeySolved) {
    // Check if an error with the same channel already exists
    const errorExists = this.state.adErrors.some((error) => error.inputKey === inputKeySolved);

    if (errorExists) {
      // Filter out the error with the matching channel
      const updatedErrors = this.state.adErrors.filter(
        (error) => error.inputKey !== inputKeySolved
      );

      this.setState({
        adErrors: updatedErrors
      });
    }
  }

  // Listens for changes to the campaign data and updates local state for campaign data
  onUpdateCampaignWithId(campaignId, updates) {
    if (this.state.campaignData?.id == campaignId) {
      this.setState({
        campaignData: Object.assign(this.state.campaignData, updates)
      });
    }
  }
  // Listens for changes to the campaign data and updates local state for campaign data
  onDeleteCampaignField(campaignId, field) {
    if (this.state.campaignData?.id == campaignId) {
      const newCampaignData = this.state.campaignData;
      delete newCampaignData?.[field];
      this.setState({
        campaignData: newCampaignData
      });
    }
  }

  // Updates url on every channel data
  onUpdateEveryChannelURL({ url, isReleased, userName, campaignId }) {
    const channelsData = isReleased ? this.state.released.channelsData : this.state.channelsData;
    const newChannelsData = { ...channelsData };

    logger.log("PreviewStore:onUpdateEveryChannelURL", "called", {
      campaignId,
      url
    });

    Object.keys(newChannelsData).forEach((channelShortName) => {
      newChannelsData[channelShortName].forEach((item) => {
        item.url = url;
      });
    });

    let newData = { channelsData: newChannelsData };

    if (isReleased) {
      newData = {
        released: { ...this.state.released, channelsData: newChannelsData },
        // copy new channel data to staging also
        channelsData: newChannelsData
      };
    }

    this.updateAdManagersDestinationLink({ channels: Object.keys(channelsData), url });

    this.setState(newData);

    // Update db
    this.updatePostsDB({ released: isReleased, staging: true, userName });
  }

  updateAdManagersDestinationLink({ channels, url }) {
    if (!channels.length) {
      return;
    }

    const { campaignData, adsetData } = this.state;

    const campaignId = campaignData.id;
    const adSetId = adsetData?.payload?.id;
    const updates = channels.map((channel) => ({
      channel,
      destinationLink: url
    }));

    if (campaignId && adSetId) {
      this.setState({ updatingDestinationLink: true });

      const ref = Actions.updateDestinationLink.completed.listen(() => {
        this.setState({ updatingDestinationLink: false });

        Actions.addNotification(t("The campaign link was successfully updated"));
        ref();
      });

      const refFailed = Actions.updateDestinationLink.failed.listen(() => {
        this.setState({ updatingDestinationLink: false });

        Actions.addNotification(t("There was an error updating the campaign link"), "error");

        refFailed();
      });

      Actions.updateDestinationLink(campaignId, adsetData?.payload?.id, updates);
    }
  }

  // Updates channel data for a given post
  onUpdateChannelData({
    channelShortName,
    value,
    key,
    isReleased,
    userName,
    postNumber,
    campaignId
  }) {
    logger.debug("PreviewStore:onUpdateChannelData", "called", {
      channelShortName,
      value,
      key,
      isReleased,
      userName,
      postNumber,
      campaignId
    });

    const channelsData = isReleased ? this.state.released.channelsData : this.state.channelsData;

    const updatedChannelCategory = channelsByShortName[channelShortName].category;
    const newChannelsData = { ...channelsData };

    Object.keys(newChannelsData).forEach((channel) => {
      logger.debug("PreviewStore:onUpdateChannelData:each", "channel", {
        channel,
        campaignId
      });

      const editableChannelData =
        newChannelsData[channel].find((channel) => {
          return channel.postNumber === postNumber;
        }) ?? newChannelsData[channel]; // If there is no channelData for this post, use the channelData for the whole channel this is for old campaigns

      if (channel === channelShortName) {
        editableChannelData[key] = value;
        editableChannelData.editedStates = {
          ...(editableChannelData.editedStates || {}),
          [key]: true
        };
      } else if (
        updatedChannelCategory != null &&
        channelsByShortName[channel].category === updatedChannelCategory &&
        !editableChannelData.editedStates?.[key] &&
        key !== "url"
      ) {
        // channel data not edit and it has the same group as the edited channel
        editableChannelData[key] = value; // copy updates to unedited posts with the same channel category
      }
    });

    if (CHANNELS_WITH_DYNAMIC_ADS.includes(channelShortName)) {
      // If we have both Facebook and instagram and we are changing the URL/fb page, make sure the fb page is updated on both
      // channels
      if (META_CHANNELS.includes(channelShortName) && META_SHARED_ATTRIBUTES.includes(key)) {
        if (newChannelsData["facebook"]) {
          newChannelsData["facebook"].forEach((item) => {
            item[key] = value;
          });
        }

        if (newChannelsData["instagram"]) {
          newChannelsData["instagram"].forEach((item) => {
            item[key] = value;
          });
        }
      } else if (key === "url") {
        newChannelsData[channelShortName].forEach((item) => {
          item[key] = value;
        });
      }
    }

    logger.log("PreviewStore:onUpdateChannelData", "saving channel data to state", {
      released: { ...this.state.released },
      isReleased,
      newChannelsData,
      postNumber,
      campaignId
    });

    let newState = { channelsData: newChannelsData };
    if (isReleased) {
      newState = {
        released: { ...this.state.released, channelsData: newChannelsData },
        // copy new channel data to staging also
        channelsData: newChannelsData
      };
    }

    this.setState(newState);

    this.updatePostsDB({ released: isReleased, staging: true, userName });
  }

  async onUpdatePost({
    channelShortName,
    postNumber,
    updateData = {}, // {creatives: [], mediaAssets: [], ...}
    isReleased,
    userName,
    campaignId
  }) {
    logger.debug("PreviewStore:onUpdatePost", "called", {
      channelShortName,
      postNumber,
      isReleased,
      userName,
      updateData,
      campaignId
    });
    const posts = isReleased ? this.state.released.posts : this.state.posts;
    const updatedChannelCategory = channelsByShortName[channelShortName].category;

    let newPosts = posts;
    for (let key of Object.keys(updateData)) {
      newPosts = await prepareNewPosts({
        posts: newPosts,
        updatedChannelCategory,
        key,
        value: updateData[key],
        channelShortName,
        postNumber
      });
    }

    // TODO: Handle deploy post for multiple fields
    if (isReleased) {
      logger.log("PreviewStore:onUpdatePost", "isReleased case", {
        campaignId,
        newPosts
      });

      // copy everything from released to staging
      const { posts: newStagingPosts, channelsData: newStagingChannelsData } =
        await this.deployPosts({
          userName,
          posts: newPosts,
          channelsData: this.state.released.channelsData,
          type: "staging"
        });

      logger.debug("PreviewStore:onUpdatePost", "called", {
        newStagingPosts,
        campaignId,
        newStagingChannelsData
      });

      this.setState({
        released: { ...this.state.released, posts: newPosts },
        posts: newStagingPosts,
        channelsData: newStagingChannelsData,
        isReleased
      });
    } else {
      logger.log(
        "PreviewStore:onUpdatePost",
        "posts not releases yet, just set the state and not deploy",
        {
          newPosts,
          campaignId
        }
      );

      this.setState({ posts: newPosts });
    }

    this.updatePostsDB({ released: isReleased, staging: true, userName });
  }

  async onUpdatePostMediaLab({
    assetIdMap,
    existingPosts,
    creativeAssets,
    userName,
    campaignId,
    isReleased
  }) {
    for (const [channelShortName, postsMap] of Object.entries(assetIdMap)) {
      for (const [postMapNumber, ids] of Object.entries(postsMap)) {
        const existingMediaAssets = existingPosts[channelShortName]?.filter(
          ({ postNumber }) => Number(postNumber) === Number(postMapNumber)
        )[0].mediaAssets;
        const existingCreatives = existingPosts[channelShortName]?.filter(
          ({ postNumber }) => Number(postNumber) === Number(postMapNumber)
        )[0].creatives;

        const newMediaAssets = existingMediaAssets ? existingMediaAssets.concat(ids) : ids;
        const newCreatives = existingCreatives
          ? existingCreatives.concat(creativeAssets.slice(0, ids.length))
          : creativeAssets;
        const updatePostObject = {
          campaignId,
          channelShortName: channelShortName,
          isReleased,
          postNumber: Number(postMapNumber),
          updateData: {
            creatives: newCreatives,
            mediaAssets: newMediaAssets
          },
          userName
        };
        const { updateData = {} } = updatePostObject;
        logger.debug("PreviewStore:onUpdatePostMediaLab", "called", {
          channelShortName,
          postMapNumber,
          isReleased,
          userName,
          updateData,
          campaignId
        });
        const posts = isReleased ? this.state.released.posts : this.state.posts;
        const updatedChannelCategory = channelsByShortName[channelShortName].category;
        let newPosts = posts;
        for (let key of Object.keys(updateData)) {
          newPosts = await prepareNewPosts({
            posts: newPosts,
            updatedChannelCategory,
            key,
            value: updateData[key],
            channelShortName,
            postNumber: postMapNumber
          });
        }
        // TODO: Handle deploy post for multiple fields
        if (isReleased) {
          logger.log("PreviewStore:onUpdatePost", "isReleased case", {
            campaignId,
            newPosts
          });
          // copy everything from released to staging
          const { posts: newStagingPosts, channelsData: newStagingChannelsData } =
            await this.deployPosts({
              userName,
              posts: newPosts,
              channelsData: this.state.released.channelsData,
              type: "staging"
            });
          logger.debug("PreviewStore:onUpdatePost", "called", {
            newStagingPosts,
            campaignId,
            newStagingChannelsData
          });
          this.setState({
            released: { ...this.state.released, posts: newPosts },
            posts: newStagingPosts,
            channelsData: newStagingChannelsData,
            isReleased
          });
        } else {
          logger.log(
            "PreviewStore:onUpdatePost",
            "posts not releases yet, just set the state and not deploy",
            {
              newPosts,
              campaignId
            }
          );
          this.setState({ posts: newPosts });
        }
        this.updatePostsDB({ released: isReleased, staging: true, userName });
      }
    }
  }

  concatChannelDataByChannelName(channelData, channelShortName) {
    // Find the maximum postNumber among existing objects
    const lastPostNumber = Math.max(
      ...channelData[channelShortName].map((obj) => obj.postNumber || 0),
      0
    );

    // Create a new object based on the properties of existing objects using the spread operator
    const newChannelData = {
      ...channelData[channelShortName][0], // Create new object based on the first channelData
      postNumber: lastPostNumber + 1 // Increment the postNumber
    };

    // Concatenate the new object to the array
    channelData[channelShortName] = channelData[channelShortName].concat(newChannelData);

    return channelData;
  }

  async onAddPost({ channelShortName, postNumber, isReleased, userName, campaignId }) {
    logger.log("PreviewStore:onAddPost", "calling...", {
      channelShortName,
      postNumber,
      isReleased,
      userName,
      campaignId
    });

    const posts = isReleased ? this.state.released.posts : this.state.posts;
    const newPost = {
      ...posts[channelShortName][0],
      editedStates: {
        teaserText: false,
        jobPostingMarkup: false,
        logo: false,
        creatives: false
      }, // new posts are unedited and will copy updates from others until edited
      channel: channelShortName,
      updatedDate: null,
      lastUpdatedBy: null,
      postNumber
    };

    logger.log("PreviewStore:onAddPost", "called", {
      newPost,
      campaignId
    });

    // Duplicate media assets
    if (newPost.mediaAssets?.length > 0) {
      const duplicateMediaAssets = async (mediaAssetIds) => {
        let newMediaAssets = [];

        const context = {
          failed: (err) => {
            return newMediaAssets;
          },
          completed: ({ payload }) => {
            newMediaAssets = payload;
            return newMediaAssets;
          }
        };

        const mediaAssetsIdsToDuplicate = { mediaAssetIds: mediaAssetIds };
        await callWebUtils(context, "duplicateMultipleMediaAssets", [mediaAssetsIdsToDuplicate]);
        return newMediaAssets;
      };

      const oldMediaAssetsIds = newPost.mediaAssets?.map((mediaAssetId) => mediaAssetId) || [];

      try {
        const newMediaAssetIds =
          oldMediaAssetsIds.length > 0
            ? await duplicateMediaAssets(oldMediaAssetsIds).then((result) => {
                return result.duplicatedMediaAssetIds;
              })
            : [];

        if (newMediaAssetIds) {
          newPost.mediaAssets = newMediaAssetIds;
        }
      } catch (error) {
        logger.error("PreviewStore:onAddPost", "duplicating media assets", {
          campaignId,
          error
        });
      }
    }

    if (this.state.campaignData.id != null) {
      // also update db if campaign already exists
      const channelsData = isReleased ? this.state.released.channelsData : this.state.channelsData;
      const createdDate = new Date().toISOString();

      // Copy data from the first channelData
      const originalChannelData = channelsData[channelShortName][0];
      // Create a shallow copy of the originalChannelData
      const copiedChannelData = { ...originalChannelData };

      // Now channelData has it's own postNumber
      // We need to increment the postNumber of the originalChannelData
      copiedChannelData.postNumber = newPost.postNumber;

      newPost.channelData = copiedChannelData;
      newPost.createdDate = createdDate;
      newPost.createdBy = userName;
      newPost.type = isReleased ? "released" : "staging";

      const { id, ...newPostWithoutId } = newPost;

      const resp = await database.saveAdCreatives([newPostWithoutId]);
      newPost.id = resp[0].id;
    }

    const newPosts = {
      ...posts,
      [channelShortName]: [...posts[channelShortName], newPost]
    };

    if (isReleased) {
      logger.log("PreviewStore:onAddPost", "isReleased case", {
        campaignId
      });
      const newChannelsData = this.concatChannelDataByChannelName(
        this.state.released.channelsData,
        channelShortName
      );

      // copy everything from released to staging
      const { posts: newStagingPosts, channelsData: newStagingChannelsData } =
        await this.deployPosts({
          userName,
          posts: newPosts,
          channelsData: newChannelsData,
          type: "staging"
        });

      this.setState({
        released: { ...this.state.released, posts: newPosts, channelsData: newChannelsData },
        posts: newStagingPosts,
        channelsData: newStagingChannelsData,
        selectedPostNumber: postNumber
      });
    } else {
      Actions.updateCampaignDeployedStatus(this.state.campaignData.id, false);

      const newChannelsData = this.concatChannelDataByChannelName(
        this.state.channelsData,
        channelShortName
      );

      this.setState({
        posts: newPosts,
        selectedPostNumber: postNumber,
        channelsData: newChannelsData
      });
    }
  }

  async onRemovePost({ channelShortName, postId, postNumber, isReleased, userName, campaignId }) {
    const posts = isReleased ? this.state.released.posts : this.state.posts;

    logger.log("PreviewStore:onRemovePost", "Called", {
      campaignId
    });

    let removeId = null;
    const newPosts = {
      ...posts,
      [channelShortName]: posts[channelShortName].filter(({ id, postNumber: number }) => {
        if (!postId) {
          return number !== postNumber;
        }
        if (id !== postId) {
          return true;
        }
        removeId = id;
        return false;
      })
    };

    if (removeId) {
      database.deleteAdCreatives([removeId]);
      const deletedPostData = posts[channelShortName].filter(({ id }) => id === removeId)[0];
      if (deletedPostData) {
        PerformanceEditorActions.deleteMultipleMediaAssets({
          mediaAssetIds: deletedPostData.mediaAssets
        });
      }
    }
    if (isReleased) {
      logger.log("PreviewStore:onRemovePost", "isReleased case", {
        campaignId
      });
      // copy everything from released to staging
      const { posts: newStagingPosts, channelsData: newStagingChannelsData } =
        await this.deployPosts({
          userName,
          posts: newPosts,
          channelsData: this.state.released.channelsData,
          type: "staging"
        });
      this.setState({
        released: { ...this.state.released, posts: newPosts },
        posts: newStagingPosts,
        channelsData: newStagingChannelsData
      });
    } else {
      Actions.updateCampaignDeployedStatus(this.state.campaignData.id, false);
      this.setState({ posts: newPosts });
    }
  }

  handleTeaserTemplates(teaserTemplates = []) {
    const { templatePanel, websitePanel, historicalPanel } = teaserTemplates.reduce(
      (panelsAcc, teaserTemplate) => {
        let panel;
        if (teaserTemplate.source === "template") {
          panel = "templatePanel";
        } else if (teaserTemplate.source === "website") {
          panel = "websitePanel";
        } else if (teaserTemplate.source === "historical") {
          panel = "historicalPanel";
        }
        if (panel) {
          panelsAcc[panel][teaserTemplate.channel] = [
            ...(panelsAcc[panel][teaserTemplate.channel] || []),
            teaserTemplate.text
          ];
        }
        return panelsAcc;
      },
      { templatePanel: {}, historicalPanel: {}, websitePanel: {} }
    );
    return {
      templatePanel,
      websitePanel,
      historicalPanel
    };
  }

  onGetTeaserTextRecommendation() {
    logger.log("PreviewStore:onGetTeaserTextRecommendation", "called");
    this.setState({
      loadingTeaserOptions: true
    });
  }

  onGetTeaserTextRecommendationCompleted({ payload }) {
    logger.log("PreviewStore:onGetTeaserTextRecommendationCompleted", "fetched teaser options", {
      payload,
      time: moment().format()
    });
    const teaserOptions = this.handleTeaserTemplates(payload);
    logger.debug("PreviewStore:onGetTeaserTextRecommendationCompleted", "called", {
      teaserOptions
    });

    this.setState({
      teaserOptions,
      loadingTeaserOptions: false
    });
  }

  onGetTeaserTextRecommendationFailed(err) {
    logger.log("PreviewStore:onGetTeaserTextRecommendationFailed", "called", {
      err
    });
    this.setState({
      loadingTeaserOptions: false
    });
  }

  ////////////// Functions For User view //////////////
  onGetAdPreviewData() {
    this.setState({
      loading: true
    });
  }

  onGetAdPreviewDataCompleted({ payload }) {
    logger.log("PreviewStore:onGetAdPreviewDataCompleted", "called", {
      ...payload,
      time: moment().format()
    });
    this.setState({
      loading: false,
      payload
    });
  }

  onGetAdPreviewDataFailed(err) {
    logger.log("error:PreviewStore:onGetAdPreviewDataFailed", "called", {
      err
    });
    this.setState({
      loading: false,
      payload: {}
    });
  }

  onGenerateJobPageCompleted(result) {
    logger.log("PreviewStore:onGenerateJobPageCompleted", "finished", { ...result });
    const { payload = {} } = result;
    if (payload.atsId) {
      this.setState({
        jobPageLink: getLeadFormLink({
          jobCategoryId: "",
          campaignId: payload.atsId,
          subdomain: this.state.subdomain,
          baseUrlTalentBait: window?.config?.baseUrlTalentBait
        })
      });
    }
  }

  onLoadOrganizationOfCampaignCompleted(organization) {
    logger.log("PreviewStore:onLoadOrganizationOfCampaignCompleted", "called", {
      orgaId: organization.id
    });
    const { maxPostPerChannel } = this.state;
    this.setState({
      allowMultipleCreativesPerPost: organization.allowMultipleCreativesPerPost,
      allowMultiplePostsPerChannel: organization.allowMultiplePostsPerChannel,
      maxPostPerChannel: organization.maxPostPerChannel || maxPostPerChannel,
      companyLogo: organization.isAgency ? "" : organization.companyLogo,
      subdomain: organization.subdomain
    });
  }

  onLoadOrganizationOfCampaignFailed(error) {
    logger.log("error:PreviewStore:onLoadOrganizationOfCampaignFailed", "called", { error });
  }

  onTurnOffDefaultOpenTeaserEditor() {
    logger.log("PreviewStore:onTurnOffDefaultOpenTeaserEditor", "called");
    this.setState({
      defaultOpenTeaserEditor: false
    });
  }

  getDefaultCreatives(channel) {
    logger.log("PreviewStore:getDefaultCreatives", "called", this.state.payload);

    if (channel === "tiktok") {
      return ["/videos/default_video_creative.mp4"];
    }
    const { payload, jobCategoryData } = this.state;

    const { pageImage, suggestions, historicPictures } = payload ?? {};

    const recentPictures = getPictureArray(historicPictures, 3);

    if (pageImage?.length) {
      const creatives = pageImage.map((image) => image.image_url).slice(0, 3);

      this.setState({ defaultCreatives: creatives });
      return creatives;
    }
    if (suggestions?.length) {
      const creatives = suggestions.map((image) => image.image_url).slice(0, 3);

      this.setState({ defaultCreatives: creatives });
      return creatives;
    }
    if (recentPictures.length) {
      const creatives = recentPictures.map((image) => image.image_url).slice(0, 3);

      this.setState({
        defaultCreatives: creatives
      });
      return creatives;
    }
    if (jobCategoryData?.defaultImages?.length) {
      const creatives = jobCategoryData.defaultImages;

      this.setState({ defaultCreatives: creatives });
      return creatives;
    }
    const creatives = ["/images/bild-vorschau.png"];

    this.setState({ defaultCreatives: creatives });
    return creatives; // default fallback image
  }

  async getDefaultMediaAssets({ defaultCreatives, channel, orgaId, companyId }) {
    logger.log("PreviewStore:getDefaultMediaAssets", "called", { defaultCreatives });
    let defaultMediaAssetIds = [];

    if (CHANNELS_WITHOUT_CREATIVES.includes(channel)) {
      return [];
    }

    const context = {
      failed: (err) => {
        return defaultMediaAssetIds;
      },
      completed: ({ payload }) => {
        defaultMediaAssetIds = payload;
        return defaultMediaAssetIds;
      }
    };

    const initialMediaAssets = {
      text: t("We Want You"),
      secondaryText: t("Are you ready?"),
      CTAText: t("Apply now"),
      logo: "",
      channel,
      backgroundMedia: defaultCreatives,
      orgaId,
      companyId
    };

    await callWebUtils(context, "createMultipleMediaAssets", [initialMediaAssets]);
    return defaultMediaAssetIds;
  }

  getDefaultPageName({ channelShortName, location, cleanedTitle }) {
    const locationString = (location || []).map((item) => item.location)[0] || "Deutschland";
    const { payload } = this.state;
    if (channelShortName === "indeed" || channelShortName === "linkedin") {
      return locationString;
    } else if (channelShortName === "twitter") {
      return "JobHappier";
    } else if (payload?.pages?.[0]) {
      return payload?.pages[0].pageName;
    }
    return cleanedTitle + " Jobs";
  }

  getDefaultTitle({ channelShortName, jobTitle }) {
    return jobTitle.substring(0, channelsByShortName[channelShortName].limits.title);
  }

  getDefaultCompanyName({ channelShortName, companyName }) {
    return companyName.substring(0, channelsByShortName[channelShortName].limits.companyName);
  }

  mergeTeaserWithMainText({
    teaserText = "",
    benefits = [],
    tasks = [],
    jobTitle = "",
    campaignLanguage
  }) {
    let mainText = "";
    if (benefits && benefits.length) {
      mainText += `${t("We offer", { lng: campaignLanguage })}:\n✅`;
      mainText += benefits.join("\n✅");
      mainText += "\n";
    } else if (tasks && tasks.length) {
      mainText += `${t("Tasks", { lng: campaignLanguage })}:\n✅`;
      mainText += tasks.join("\n✅");
      mainText += "\n";
    }
    let teaser = teaserText.replace("{{jobTitle}}", jobTitle);
    teaser = `${teaser}\n\n${mainText}`;
    return teaser;
  }

  getDefaultTeaserText({ channelShortName, jobTitle, sentences, campaignLanguage }) {
    const { benefits = [], tasks = [] } = sentences || {};
    const { teaserOptions } = this.state;
    const text =
      teaserOptions.historicalPanel[channelShortName]?.[0] ||
      teaserOptions.templatePanel[channelShortName]?.[0]?.replace("{{jobTitle}}", jobTitle) ||
      // eslint-disable-next-line react-i18n/no-dynamic-translation-keys
      t(defaultTeaserTemplates[0], { lng: campaignLanguage }).replace("{{jobTitle}}", jobTitle);

    const textWithSentences = this.mergeTeaserWithMainText({
      teaserText: text,
      benefits,
      tasks,
      jobTitle,
      campaignLanguage
    });

    const filteredTest = textWithSentences.substring(
      0,
      channelsByShortName[channelShortName].limits.teaserText
    );

    return filteredTest;
  }

  async onGenerateTextRecommendationStream(body) {
    logger.log("PreviewStore:onGenerateTextRecommendationStream", "calling here", { body });
    await this.generateTextRecommendationStream(body);
    this.setState({
      isRegeneratingText: false
    });
  }

  onSetIsRegeneratingText() {
    this.setState({ isRegeneratingText: true });
  }

  async generateTextRecommendationStream(params) {
    logger.log("PreviewStore:generateTextRecommendationStream", "called", params);

    const {
      channel,
      jobTitle,
      jobPostingUrl,
      orgaName,
      orgaId,
      userId,
      userName,
      language = "German",
      formality = "Sie"
    } = params;

    CustomerSucessController.send("Description Stream Started", {
      channel: channel,
      user_id: userId,
      user_name: userName,
      organization_id: orgaId,
      organization_name: orgaName,
      job_title: jobTitle,
      job_url: jobPostingUrl
    });

    try {
      const context = {
        failed: (err) => {
          this.setState({
            isRegeneratingText: false
          });
          throw err;
        },
        completed: () => {
          this.setState({
            isRegeneratingText: false
          });
        }
      };
      let rawAutoGeneratedText = "";
      let autoGeneratedData;

      await callWebUtils(context, "generateTextRecommendationStream", [
        {
          body: {
            channel,
            jobTitle,
            jobPostingUrl,
            language,
            formality
          },
          onData: (textChunk) => {
            rawAutoGeneratedText += textChunk || "";

            if (!textChunk || !this.state.posts?.[channel]?.length) {
              return;
            }

            if (channel !== "google-search") {
              autoGeneratedData = {
                autoGeneratedText: rawAutoGeneratedText,
                teaserText: rawAutoGeneratedText
              };
            } else {
              let jsonText = rawAutoGeneratedText;
              let quotationsCount = 0;
              let openBracketsCount = 0;
              let closeBracketsCount = 0;
              let openCurlyBracesCount = 0;
              let closeCurlyBracesCount = 0;

              if (jsonText) {
                for (let i = 0; i < jsonText.length; i++) {
                  if (jsonText[i] === '"') {
                    quotationsCount++;
                  }
                  if (jsonText[i] === "[") {
                    openBracketsCount++;
                  }
                  if (jsonText[i] === "]") {
                    closeBracketsCount++;
                  }
                  if (jsonText[i] === "{") {
                    openCurlyBracesCount++;
                  }
                  if (jsonText[i] === "}") {
                    closeCurlyBracesCount++;
                  }
                }
              }

              if (quotationsCount % 2 !== 0) {
                jsonText += '"';
              }
              jsonText += Array(Math.max(0, openBracketsCount - closeBracketsCount))
                .fill("]")
                .join("");
              jsonText += Array(Math.max(0, openCurlyBracesCount - closeCurlyBracesCount))
                .fill("}")
                .join("");

              try {
                const { ad = "", headlines = [] } = JSON.parse(jsonText);
                const title = headlines[0];
                const title2 = headlines[1];
                const title3 = headlines[2];

                autoGeneratedData = {
                  autoGeneratedText: ad,
                  teaserText: ad,
                  title: title,
                  autoTitle: title,
                  title2: title2,
                  autoTitle2: title2,
                  title3: title3,
                  autoTitle3: title3
                };
              } catch (err) {
                logger.log(
                  "PreviewStore:generateTextRecommendationStream",
                  "could not parse google search stream"
                );
              }
            }

            let posts;
            if (Array.isArray(this.state.released.posts)) {
              posts = this.state.released.posts;
            } else {
              posts = this.state.posts;
            }

            const newPostsByChannels = {
              ...posts,
              [channel]: posts[channel].map((post) =>
                post.isAutoGenerated
                  ? {
                      ...post,
                      ...autoGeneratedData,
                      isAutoGenerationStreaming: true
                    }
                  : post
              )
            };

            this.setState({
              posts: newPostsByChannels,
              released: { ...this.state.released, posts: newPostsByChannels }
            });
          }
        }
      ]);

      const finalPostsByChannels = {
        ...this.state.posts,
        [channel]: this.state.posts[channel].map((post) => ({
          ...post,
          isAutoGenerationStreaming: false
        }))
      };

      this.setState({ posts: finalPostsByChannels });

      CustomerSucessController.send("Description Stream Ended", {
        status: "success",
        channel: channel,
        user_id: userId,
        user_name: userName,
        organization_id: orgaId,
        organization_name: orgaName,
        job_title: jobTitle,
        job_url: jobPostingUrl
      });
    } catch (err) {
      const fallbackText = this.getDefaultTeaserText({
        channelShortName: channel,
        jobTitle: this.state.campaignData?.jobTitle
      });

      const newPostsByChannels = {
        ...this.state.posts,
        [channel]: this.state.posts[channel].map((post) => ({
          ...post,
          autoGeneratedText: fallbackText,
          teaserText: fallbackText,
          isAutoGenerated: false,
          isAutoGenerationStreaming: false
        }))
      };

      this.setState({ posts: newPostsByChannels });
      Actions.addNotification(
        t("Currently it was not possible to generate your text using AI"),
        "info",
        false,
        6
      );

      CustomerSucessController.send("Description Stream Ended", {
        status: "error",
        channel: channel,
        user_id: userId,
        user_name: userName,
        organization_id: orgaId,
        organization_name: orgaName,
        job_title: jobTitle,
        job_url: jobPostingUrl
      });
    }
  }

  /**
   * Initializes post data for a given channel.
   * @private
   * @param {Object} params - The parameters.
   * @param {string} params.channelShortName - The short name of the channel.
   * @param {boolean} params.canAutoGenerateCampaignText - Flag indicating if campaign text can be auto-generated.
   * @param {string} params.companyLogo - The company logo URL.
   * @param {Array<string>} params.defaultLogos - Array of default logo URLs.
   * @param {string} params.jobPostingMarkup - The job posting markup.
   * @param {Array<string>} params.croppedCreative - The cropped creative URLs.
   * @param {string} params.jobTitle - The job title.
   * @param {Array<string>} params.defaultMediaAssets - Array of default media asset URLs.
   * @param {Object} params.brandKit - The brand kit.
   * @returns {Array<Object>} Array of post objects.
   */
  getInitPost({
    channelShortName,
    canAutoGenerateCampaignText,
    companyLogo,
    defaultLogos,
    jobPostingMarkup,
    croppedCreative,
    jobTitle,
    defaultMediaAssets,
    brandKit,
    sentences,
    campaignLanguage
  }) {
    const { logos } = brandKit || {};
    const { squareLogo, landscapeLogo } = logos || {};
    const newLogo = squareLogo || landscapeLogo || companyLogo || "";

    const isFacebook = channelShortName === "facebook";
    const newDefaultLogos = newLogo ? [newLogo] : defaultLogos;
    const resolvedLogos = isFacebook ? null : newDefaultLogos;
    const useEmptyFields = brandKit?.useEmptyFields;

    const isChannelWithoutCreatives = CHANNELS_WITHOUT_CREATIVES.includes(channelShortName);
    const isTikTok = channelShortName === "tiktok";

    let creatives = croppedCreative;

    if (isTikTok) {
      creatives = this.getDefaultCreatives(channelShortName);
    } else if (isChannelWithoutCreatives) {
      creatives = [];
    }

    const mediaAssets = isChannelWithoutCreatives ? [] : defaultMediaAssets;

    return [
      {
        postNumber: 1,
        channel: channelShortName,
        editedStates: {
          teaserText: false,
          logo: false,
          jobPostingMarkup: false,
          creatives: false
        },
        isAutoGenerated: canAutoGenerateCampaignText,
        teaserText: canAutoGenerateCampaignText
          ? ""
          : this.getDefaultTeaserText({ channelShortName, jobTitle, sentences, campaignLanguage }),
        teaserText2:
          channelShortName === "google-search" ? t("Apply now", { lng: campaignLanguage }) : "",
        logo: useEmptyFields ? "" : newLogo,
        logos: useEmptyFields ? null : resolvedLogos,
        jobPostingMarkup:
          channelShortName === "indeed" || channelShortName === "linkedin" ? jobPostingMarkup : "",
        creatives,
        mediaAssets
      }
    ];
  }

  /**
   * Initializes channel data for a given channel.
   * @private
   * @param {Object} params - The parameters.
   * @param {string} params.channelShortName - The short name of the channel.
   * @param {Object} params.limits - The limits for the channel.
   * @param {string|null} params.pageId - The page ID.
   * @param {string} params.pageLogo - The page logo URL.
   * @param {Array<string>} params.location - The location array.
   * @param {string} params.cleanedTitle - The cleaned job title.
   * @param {string} params.displayLink - The display link.
   * @param {string} params.jobTitle - The job title.
   * @param {string} params.companyName - The company name.
   * @param {string|null} params.jobPageLink - The job page link.
   * @param {string} params.jobPostingUrl - The job posting URL.
   * @returns {Array<Object>} Array of channel data objects.
   */
  getInitChannelData({
    channelShortName,
    limits,
    pageId,
    pageLogo,
    location,
    cleanedTitle,
    displayLink,
    jobTitle,
    companyName,
    jobPageLink,
    jobPostingUrl,
    campaignLanguage,
    orgaId
  }) {
    logger.debug("PreviewStore:getInitChannelData", "props", {
      companyName,
      orgaId
    });

    const pageProps = this.setPageProperties({
      campaignLanguage,
      channelShortName,
      location,
      cleanedTitle,
      orgaId,
      pageId,
      pageLogo
    });

    return [
      {
        editedStates: {
          pageId: false,
          pageLogo: false,
          pageName: false,
          subHeadline: false,
          displayLink: false,
          title: false,
          companyName: false,
          url: false
        },
        ...pageProps,
        displayLink: displayLink.substring(0, limits.displayLink),
        title: this.getDefaultTitle({ channelShortName, jobTitle }),
        companyName: this.getDefaultCompanyName({ channelShortName, companyName }),
        url: jobPageLink || jobPostingUrl,
        postNumber: 1,
        channel: channelShortName,
        subHeadline: t("Looking for a new challenge?", { lng: campaignLanguage }).substring(
          0,
          limits.subHeadline
        )
      }
    ];
  }

  setPageProperties = ({
    campaignLanguage,
    channelShortName,
    location,
    cleanedTitle,
    orgaId,
    pageId,
    pageLogo
  }) => {
    const nonGermanCampaign = ["es", "en"].includes(campaignLanguage);

    // Future change:
    // Return defaultPage object if it exists in the organisation.
    if (orgaId === "6ltNxhShJVC0sI3AQytW") {
      return DEFAULT_JOB_GUARDIAN_PAGE;
    }
    if (orgaId === "QkrhIjNL7vC9mWjfJTui") {
      return DEFAULT_HILTE_JEHLE_PAGE;
    }
    if (nonGermanCampaign) {
      return DEFAULT_PAGE;
    }

    return {
      pageId,
      pageLogo,
      pageName: this.getDefaultPageName({ channelShortName, location, cleanedTitle })
    };
  };

  /**
   * Retrieves the cropped creative URLs for a given channel.
   * @private
   * @param {Array<Object>} imageSpecs - The image specifications.
   * @param {Array<string>} defaultCreatives - The default creative URLs.
   * @param {string} channelShortName - The short name of the channel.
   * @returns {Array<string>} Array of cropped creative URLs.
   */
  getCroppedCreativeByChannel(imageSpecs, defaultCreatives, channelShortName) {
    if (CHANNELS_WITHOUT_CREATIVES.includes(channelShortName)) {
      return [];
    }

    return imageSpecs.map((imageSpec, index) => {
      let creativeData = defaultCreatives[index];
      if (imageSpec) {
        creativeData =
          getCreativeUrlForOtherChannel({
            channel: channelShortName,
            imageSpecs: imageSpec,
            imageUrl: defaultCreatives[index]
          })?.creatives?.[0] || defaultCreatives[index];
      }
      return creativeData;
    });
  }

  /**
   * Initializes posts and channels data.
   * @public
   * @param {Object} params - The parameters.
   * @param {string} params.jobPostingUrl - The job posting URL.
   * @param {string} params.jobTitle - The job title.
   * @param {string} params.companyName - The company name.
   * @param {Array<Object>} params.selectedChannels - The selected channels.
   * @param {string} params.orgaName - The organization name.
   * @param {string} params.orgaId - The organization ID.
   * @param {string} params.userId - The user ID.
   * @param {string} params.userName - The user name.
   * @param {string} params.jobCategoryId - The job category ID.
   * @param {Array<string>} [params.location=[]] - The location array.
   * @param {boolean} params.shouldAutoGenerateCampaignText - Flag indicating if campaign text should be auto-generated.
   * @param {Object} params.brandKit - The brand kit.
   * @param {{ benefits, tasks, requirements }} params.sentences - The sentences.
   * @param {string} params.locale - The locale.
   */
  async initPosts({
    jobPostingUrl,
    jobTitle,
    companyName,
    companyId,
    selectedChannels,
    orgaName,
    orgaId,
    userId,
    userName,
    jobCategoryId,
    location = [],
    shouldAutoGenerateCampaignText,
    brandKit,
    sentences,
    campaignLanguage
  }) {
    logger.log("PreviewStore:initPosts", "called", {
      payload: this.state.payload,
      jobPostingUrl,
      jobTitle,
      companyName,
      companyId,
      selectedChannels,
      companyLogo: this.state.companyLogo,
      orgaName,
      posts: this.state.posts,
      shouldAutoGenerateCampaignText,
      brandKit,
      sentences,
      campaignLanguage
    });

    let displayLink = "Angezeigter Link";
    try {
      displayLink = getDisplayLink(jobPostingUrl);
    } catch (_) {
      logger.log("error:PreviewStore:Error getting display link");
    }

    const cleanedTitle = cleanTitle(jobTitle);
    const jobTitleIcon = cleanedTitle
      .toUpperCase()
      .split(/ |\/|-/)
      .join("+");

    const { companyLogo = "", jobPageLink, payload } = this.state;
    const posts = {};
    const channelsData = {};

    const defaultCreatives = this.getDefaultCreatives();
    const defaultLogos = defaultCreatives.map(() => companyLogo);

    const imageSpecs = [];
    for (let creative of defaultCreatives) {
      imageSpecs.push(await getImageSpecs(creative || ""));
    }

    logger.log("PreviewStore:initPosts", "about to check for each channel", {
      selectedChannels,
      payload,
      campaignLanguage
    });

    for (const channel of selectedChannels) {
      const channelShortName = channel.channel_short_name;
      const canAutoGenerateCampaignText = Boolean(
        !chatgptDisabledChannels.includes(channelShortName) && shouldAutoGenerateCampaignText
      );

      const croppedCreative = this.getCroppedCreativeByChannel(
        imageSpecs,
        defaultCreatives,
        channelShortName
      );

      const limits = channelsByShortName[channelShortName].limits;
      const existingPost = this.state.posts?.[channelShortName];
      const existingChannelData = this.state.channelsData?.[channelShortName];
      const pageId = payload?.pages?.[0]?.pageId || null;
      const pageLogo = payload?.pages?.[0]
        ? `http://graph.facebook.com/${pageId}/picture?type=square`
        : `https://eu.ui-avatars.com/api/?name=${jobTitleIcon}`;

      const hasPosts = existingPost && existingPost.length > 0;
      const processedDefaultCreatives = hasPosts ? existingPost[0].creatives : croppedCreative;

      const defaultMediaAssets = await this.getDefaultMediaAssets({
        defaultCreatives: processedDefaultCreatives,
        channel: channelShortName,
        orgaId,
        companyId
      });

      // Post data
      posts[channelShortName] =
        existingPost ||
        this.getInitPost({
          channelShortName,
          canAutoGenerateCampaignText,
          companyLogo,
          defaultLogos,
          payload,
          jobPostingMarkup: payload?.jobPostingMarkup || "",
          croppedCreative,
          jobTitle,
          defaultMediaAssets,
          brandKit,
          sentences,
          campaignLanguage
        });

      const language = getLanguageByLocale(campaignLanguage);
      if (!existingPost && canAutoGenerateCampaignText) {
        this.generateTextRecommendationStream({
          channel: channelShortName,
          orgaName,
          orgaId,
          userId,
          userName,
          jobTitle,
          jobPostingUrl,
          language
        });
      }

      // Channel data
      channelsData[channelShortName] =
        existingChannelData ||
        this.getInitChannelData({
          channelShortName,
          limits,
          pageId,
          pageLogo,
          location,
          cleanedTitle,
          displayLink,
          jobTitle,
          companyName,
          jobPageLink,
          jobPostingUrl,
          campaignLanguage,
          orgaId
        });
    }

    logger.log("PreviewStore:initPosts", "finished generating posts", {
      posts,
      channelsData
    });

    this.setState({
      campaignData: {
        ...this.state.campaignData,
        jobPostingUrl,
        jobTitle,
        companyName,
        companyId,
        jobCategoryId,
        orgaName
      },
      posts,
      channelsData
    });
  }

  ////////////// End of User view functions //////////////

  ////////////// Functions For Edit view //////////////

  onGetAllFacebookPages() {
    this.setState({
      loadingFacebookPages: true
    });
  }

  onGetAllFacebookPagesCompleted(facebookPages) {
    facebookPages = (facebookPages || []).filter((page) => !page.isDisabled);
    this.setState({
      loadingFacebookPages: false,
      facebookPages
    });
  }

  onGetAllFacebookPagesFailed(err) {
    logger.log("error:PreviewStore:onGetAllFacebookPagesFailed", "called", err);
    this.setState({
      loadingFacebookPages: false
    });
  }

  onGetNewFacebookPages() {
    this.setState({
      loadingFacebookPages: true
    });
  }
  onGetNewFacebookPagesCompleted(facebookPages) {
    logger.log("PreviewStore:onGetNewFacebookPagesCompleted", "called", { facebookPages });
    facebookPages = (facebookPages?.payload || []).filter((page) => !page.isDisabled);
    this.setState({
      loadingFacebookPages: false,
      facebookPages
    });
  }
  onGetNewFacebookPagesFailed(error) {
    logger.log("error:PreviewStore:onGetNewFacebookPagesFailed", "called", { error });
    this.setState({
      loadingFacebookPages: false
    });
  }

  onGetAdsetsByCampaignIdPublicCompleted({ payload: adsets }) {
    logger.debug("PreviewStore:onGetAdsetsByCampaignIdPublicCompleted", "called", { adsets });
    this.setState({ adsets });
  }

  onGetAdsetsByCampaignIdPublicFailed(error) {
    logger.log("error:PreviewStore:onGetAdsetsByCampaignIdPublicFailed", "called", {
      error
    });
  }

  onGetLastLinkedInAdCreativesByCompanyAndOrgaId(companyName, orgaId) {
    logger.debug("PreviewStore:onGetLastLinkedInAdCreativesByCompanyAndOrgaIdCompleted", "called", {
      companyName,
      orgaId
    });
  }

  onGetLastLinkedInAdCreativesByCompanyAndOrgaIdCompleted(result) {
    logger.debug("PreviewStore:onGetLastLinkedInAdCreativesByCompanyAndOrgaIdCompleted", "called", {
      result
    });

    const _linkedInPageId = result.length > 0 ? result[0].channelData.linkedInCompanyId : null;
    this.setState({ linkedInPageId: _linkedInPageId });
  }

  onGetLastLinkedInAdCreativesByCompanyAndOrgaIdFailed(error) {
    logger.log(
      "error:PreviewStore:onGetLastLinkedInAdCreativesByCompanyAndOrgaIdFailed",
      "called",
      {
        error
      }
    );
  }

  onLoadAdsetDataPublicCompleted(adsetData) {
    logger.debug("PreviewStore:onLoadAdsetDataPublicCompleted", "called", adsetData);
    this.setState({ adsetData });
  }

  onLoadAdsetDataPublicFailed(error) {
    logger.log("error:PreviewStore:onLoadAdsetDataPublicCompleted", "called", error);
  }

  onGetJobCategoryWithIdCompleted(jobCategoryData) {
    logger.log("PreviewStore:onGetJobCategoryWithIdCompleted:jobCat", "called", {
      ...jobCategoryData
    });
    this.setState({ jobCategoryData });
  }

  onGetJobCategoryWithIdFailed(error) {
    logger.log("error:PreviewStore:onGetJobCategoryWithIdFailed", "failed", {
      error
    });
  }

  onGetCampaignAndAdCreativesByCampaignId(campaignId) {
    logger.debug("PreviewStore:onGetCampaignAndAdCreativesByCampaignId", "called", {
      campaignId
    });
    this.setState({
      loading: true
    });
  }

  onGetAdCreativesByCampaignIdCompleted(posts) {
    logger.log("PreviewStore:onGetAdCreativesByCampaignIdCompleted", "called", {
      posts,
      campaignId: this.state.campaignData.id
    });

    const postsPreviewData = dbPostsToPreviewData({
      posts,
      channelsToUse: this.state.campaignData?.selectedChannels?.map(
        ({ channel_short_name }) => channel_short_name
      ),
      companyId: this.state.companyId
    });

    this.setState({
      ...postsPreviewData
    });
  }

  async onGetCampaignAndAdCreativesByCampaignIdCompleted({ posts, campaignData }) {
    logger.log("PreviewStore:onGetCampaignAndAdCreativesByCampaignIdCompleted", "called", {
      posts,
      campaignData
    });
    if (!posts?.length) {
      this.setState({
        loading: false
      });
      return;
    }

    const { jobPageLink, postsPreviewData } = await formatPostPreviewData({
      campaignData,
      posts,
      companyId: this.state.companyId
    });

    logger.debug("PreviewStore:onGetCampaignAndAdCreativesByCampaignIdCompleted", "called", {
      postsPreviewData: postsPreviewData,
      campaignId: campaignData.id
    });

    this.setState({
      loading: false,
      campaignData: { ...campaignData, jobCategoryId: campaignData.jobCategoryId || "generic" },
      jobPageLink,
      ...postsPreviewData
    });
  }

  onGetCampaignAndAdCreativesByCampaignIdFailed(err) {
    logger.log("error:PreviewStore:onGetCampaignAndAdCreativesByCampaignIdFailed", "called", {
      err
    });
    this.setState({
      loading: false
    });
  }

  onLoadSingleCampaignCompleted(campaignData) {
    logger.debug("PreviewStore:onLoadSingleCampaignCompleted", "called..: ", {
      campaignId: campaignData?.id
    });
    this.setState({
      campaignData
    });
  }

  onLoadSharedCampaignData() {
    logger.debug("PreviewStore:onLoadSharedCampaignData", "called");
    this.setState({
      loading: true
    });
  }

  async onLoadSharedCampaignDataCompleted({ payload: { campaign, adCreatives: posts } }) {
    logger.debug("PreviewStore:onLoadSharedCampaignDataCompleted", "called", {
      campaignId: campaign?.id,
      campaign,
      posts
    });

    const { jobPageLink, postsPreviewData } = await formatPostPreviewData({
      campaignData: campaign,
      posts,
      companyId: this.state.companyId
    });

    logger.log("PreviewStore:onLoadSharedCampaignDataCompleted", "called", {
      campaignData: campaign,
      jobPageLink,
      ...postsPreviewData
    });

    this.setState({
      loading: false,
      campaignData: campaign,
      jobPageLink,
      ...postsPreviewData
    });
  }

  onLoadSharedCampaignDataFailed(error) {
    logger.log("error:PreviewStore:onLoadSharedCampaignDataFailed", "called", {
      error
    });
    this.setState({
      loading: false
    });
  }

  onGetAllCampaignManagersCompleted(result) {
    logger.debug("PreviewStore:onGetAllCampaignManagersCompleted", "called... ", {
      campaignManagers: result?.payload
    });
    this.setState({
      campaignManagers: result?.payload
    });
  }

  onUpdateCampaignCreationLoadingState(loadingState) {
    logger.log("PreviewStore", "onUpdateCampaignCreationLoadingState", { loadingState });
    this.setState({
      campaignCreationLoadingState: { ...this.state.campaignCreationLoadingState, ...loadingState }
    });
  }

  onUpdatePreviewCampaignData(campaignData) {
    logger.log("PreviewStore:onUpdatePreviewCampaignData", "called");
    this.setState({
      campaignData: { ...this.state.campaignData, ...campaignData }
    });
  }

  onUpdateCampaignDeployedStatusCompleted(status) {
    logger.log("PreviewStore:onUpdateCampaignDeployedStatusCompleted", "called");
    this.setState({
      campaignData: { ...this.state.campaignData, deployed: status }
    });
  }

  onUpdateCampaignStatusFailed(error) {
    logger.log("error:PreviewStore:onUpdateCampaignStatusFailed", "called", { error });
    Actions.addNotification(
      "Status wurde nicht aktualisiert. Bitte versuchen Sie nochmal",
      "error",
      true
    );
  }

  onUpdateSelectedPostNumber(selectedPostNumber) {
    this.setState({
      selectedPostNumber
    });
  }

  onUpdateSelectedPostData(selectedPostData) {
    this.setState({
      selectedPostData
    });
  }

  onCampaignCreateResetData() {
    const newState = Object.assign({}, defaultState);
    logger.log("PreviewStore:onCampaignCreateResetData", "called...", { newState });
    this.setState(newState);
  }

  onResetPreviewData() {
    const newState = Object.assign({}, defaultState);
    logger.log("PreviewStore:onResetPreviewData", "called...");
    this.setState(newState);
  }

  onLoadDraftCampaignCompleted(campaign) {
    logger.log("PreviewStore:onLoadDraftCampaignCompleted", "called", {
      campaign
    });

    this.setState({
      campaignData: { ...campaign }
    });
  }

  ////////////// End of Admin view functions //////////////
}
